import { useEffect } from 'react';
import { Select, ConfigProvider } from 'antd';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { Link } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';

import Timeline from '../DashboardTimeline/components/Timeline';
import useTimelineLogic from '../DashboardTimeline/hooks/useTimelineLogic';

import DateSelector, { DateSelectorProps } from './DateSelector';

import './style.css';
import useP4RContext from '@/store/P4RContext';
import { useGetLicenceArea } from '@/data/hooks';
import appTheme from '@/styles/theme';
import { createStartDate } from '@/utils/dateUtils';

const { Option } = Select;

export default function EmbeddedTimeline({
  startDate,
  endDate,
  comparison,
  toCompare,
  controlDisabled,
  licenceAreas,
  districts,
  currentDistrictId,
  onDistrictChange,
  customContent,
  defaultMode,
  dateSelectorOptions,
  faultBandSlot,
}: {
  startDate?: Dayjs;
  endDate?: Dayjs;
  comparison?: boolean;
  toCompare?: boolean;
  controlDisabled?: boolean;
  licenceAreas?: ILicenceAreaMin[];
  districts?: ILicenceAreaDistrict[];
  currentDistrictId?: string;
  onDistrictChange?: (districtId: string) => void;
  customContent?: React.ReactNode;
  defaultMode?: 'live' | 'replay' | 'historical';
  dateSelectorOptions?: DateSelectorProps;
  faultBandSlot?: React.ReactNode;
}) {
  const {
    selectedLicenceArea,
    currentTime,
    daysNumber,
    marks,
    handleCurrentTime,
    handleTimeLinePlay,
    changeLicenceAreaSelected,
    changeEventLicenceAreaSelected,
  } = useTimelineLogic(
    startDate,
    endDate,
    comparison,
    toCompare,
    controlDisabled,
  );

  const { timelineMode, changeTimelineMode, changeStartDate } = useP4RContext();

  // Determine if DateSelector should be shown
  // Show if: no custom content AND (dateSelectorOptions provided OR not in historical mode)
  const showDateSelector =
    !customContent && (dateSelectorOptions || timelineMode !== 'historical');

  // Effect: set context timeline mode if defaultMode is provided through props
  useEffect(() => {
    if (defaultMode) {
      changeTimelineMode(defaultMode);
    }

    // Cleanup: restore live mode and reset startDate if unmounting from historical mode
    return () => {
      if (defaultMode === 'historical') {
        changeTimelineMode('live');
        changeStartDate(createStartDate());
      }
    };
  }, [defaultMode, changeTimelineMode, changeStartDate]);

  // Effect: Auto-refresh startDate in live mode
  useEffect(() => {
    // Update startDate to current time
    const updateStartDate = () => {
      const newStartDate = createStartDate();
      changeStartDate(newStartDate);
    };

    // If timelineMode is live, set up interval to update startDate every minute
    let intervalId: NodeJS.Timeout;
    if (timelineMode === 'live') {
      // Initial call
      updateStartDate();
      // Set up interval to update every minute
      intervalId = setInterval(updateStartDate, 60 * 1000);
    }

    // Cleanup interval on component unmount or mode change
    return () => {
      clearInterval(intervalId);
    };
  }, [timelineMode, changeStartDate]);

  const { data: licenceArea } = useGetLicenceArea(selectedLicenceArea);

  let sliderClass = 'EmbeddedTimeline-slider-container';

  if (controlDisabled) sliderClass += ' controlDisabled';

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary:
            timelineMode === 'live'
              ? appTheme.token.colorPrimary
              : appTheme.token.colorSecondary,
          colorLink:
            timelineMode === 'live'
              ? appTheme.token.colorPrimary
              : appTheme.token.colorSecondary,
        },
      }}
    >
      <div className="EmbeddedTimeline">
        {licenceAreas && (
          <div className="EmbeddedTimeline__select" style={{ top: '40px' }}>
            <Select
              id="select-area"
              value={selectedLicenceArea}
              onChange={
                comparison
                  ? changeEventLicenceAreaSelected
                  : changeLicenceAreaSelected
              }
              placeholder={t('selectLicenceArea')}
              className="EmbeddedTimeline__select-input"
            >
              {licenceAreas.length &&
                licenceAreas.map((value: ILicenceAreaMin) => (
                  <Option key={value.id} value={value.id}>
                    {value.fullName}
                  </Option>
                ))}
            </Select>
            {faultBandSlot}
          </div>
        )}
        {districts && districts.length > 0 && (
          <div className="EmbeddedTimeline__select">
            <Link to="/licence-area" className="EmbeddedTimeline__back-button">
              <h4>
                <LeftOutlined />
                {t('BackToLicenceAreaDashboard', {
                  licenceArea: licenceArea?.fullName,
                })}
              </h4>
            </Link>
            <Select
              id="select-district"
              value={currentDistrictId}
              onChange={onDistrictChange}
              placeholder={t('selectDistrict')}
              className="EmbeddedTimeline__select-input"
            >
              {districts.map((district: ILicenceAreaDistrict) => (
                <Option key={district.districtId} value={district.districtId}>
                  {district.districtName}
                </Option>
              ))}
            </Select>
            {faultBandSlot}
          </div>
        )}
        <div className="EmbeddedTimeline__timeline">
          <Timeline
            key={timelineMode} // Force re-render when timeline mode changes to work around a bug in ant desing not updating the position of the slider tooltip
            startDate={startDate}
            comparison={comparison}
            currentTime={currentTime}
            daysNumber={daysNumber}
            marks={marks}
            sliderClass={sliderClass}
            handleCurrentTime={handleCurrentTime}
            handleTimeLinePlay={handleTimeLinePlay}
            controlDisabled={controlDisabled}
          />

          {/* Show custom content if provided, otherwise show DateSelector if not in historical mode */}
          {customContent ||
            (showDateSelector && <DateSelector {...dateSelectorOptions} />)}
        </div>
      </div>
    </ConfigProvider>
  );
}
